import { AfterViewInit, ChangeDetectorRef, Component, input, OnDestroy, ViewChild, inject } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { NgxExtendedPdfViewerComponent, NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { FormsModule } from '@angular/forms';
import { LoaderComponent } from '../loader/loader.component';
import { toLower } from 'lodash-es';

// Disable auto-linking to prevent @angular/fire Util.intersect error
pdfDefaultOptions.enableAutoLinking = false;

@Component({
  selector: 'app-pdf-viewer',
  imports: [NgxExtendedPdfViewerModule, FormsModule, LoaderComponent, NgOptimizedImage],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: '../../../../static/styles/components/pdf-viewer.component.scss',
})
export class PdfViewerComponent implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('pdfViewerRef', { static: false }) pdfViewer!: NgxExtendedPdfViewerComponent;

  url = input<string>('');
  currentPage = 1;
  zoom: string | number = 'page-fit';
  pdfLoaded = false;
  pdfError = false;
  errorMessage = '';
  showPdfViewer = true; // Used to force re-render
  private loadingTimeout?: number;

  ngAfterViewInit(): void {
    // Set a timeout to show error if PDF doesn't load within 5 seconds
    this.loadingTimeout = globalThis.setTimeout(() => {
      if (!this.pdfLoaded && !this.pdfError) {
        this.onPdfLoadError(
          new Error('PDF loading timed out. The file may be inaccessible or there may be a network issue.'),
        );
      }
    }, 8000) as unknown as number;
  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
  }

  onPdfLoaded(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    this.pdfLoaded = true;
    this.pdfError = false;
    this.errorMessage = '';
  }

  onPdfLoadError(error: unknown): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    this.pdfLoaded = false;
    this.pdfError = true;

    // Type narrowing and error message extraction
    if (error instanceof Error) {
      if (error.message?.includes('404')) {
        this.errorMessage = 'File could not be found.';
      } else if (toLower(error.message)?.includes('invalid')) {
        this.errorMessage = 'File is invalid or corrupted.';
      } else {
        this.errorMessage = error.message;
      }
    } else {
      this.errorMessage = 'Failed to load PDF';
    }

    // Manually trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Retry loading the PDF
   */
  retryLoad(): void {
    this.pdfError = false;
    this.pdfLoaded = false;
    this.errorMessage = '';
    // Force re-render by toggling the viewer
    this.showPdfViewer = false;
    this.cdr.detectChanges();
    this.showPdfViewer = true;
  }

  previousPage(): void {
    const PDFViewerApplication = (
      globalThis as unknown as { PDFViewerApplication?: { pdfViewer?: { currentPageNumber: number } } }
    ).PDFViewerApplication;
    if (PDFViewerApplication?.pdfViewer) {
      PDFViewerApplication.pdfViewer.currentPageNumber--;
      this.currentPage = PDFViewerApplication.pdfViewer.currentPageNumber;
    }
  }

  nextPage(): void {
    const PDFViewerApplication = (
      globalThis as unknown as { PDFViewerApplication?: { pdfViewer?: { currentPageNumber: number } } }
    ).PDFViewerApplication;
    if (PDFViewerApplication?.pdfViewer) {
      PDFViewerApplication.pdfViewer.currentPageNumber++;
      this.currentPage = PDFViewerApplication.pdfViewer.currentPageNumber;
    }
  }

  onSelectZoomChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const value = select.value;

    // Convert numeric string values to numbers
    if (!Number.isNaN(Number(value)) && value !== '') {
      this.zoom = Number(value);
    } else {
      this.zoom = value;
    }
  }

  onPageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const pageNumber = Number.parseInt(input.value, 10);
    if (pageNumber > 0) {
      this.currentPage = pageNumber;
      // The page change will be handled by the viewer through property binding
      const PDFViewerApplication = (
        globalThis as unknown as { PDFViewerApplication?: { pdfViewer?: { currentPageNumber: number } } }
      ).PDFViewerApplication;
      if (PDFViewerApplication?.pdfViewer) {
        PDFViewerApplication.pdfViewer.currentPageNumber = pageNumber;
      }
    }
  }

  rotateCounterClockwise(): void {
    const PDFViewerApplication = (
      globalThis as unknown as { PDFViewerApplication?: { pdfViewer?: { pagesRotation: number } } }
    ).PDFViewerApplication;
    if (PDFViewerApplication?.pdfViewer) {
      PDFViewerApplication.pdfViewer.pagesRotation -= 90;
    }
  }

  rotateClockwise(): void {
    const PDFViewerApplication = (
      globalThis as unknown as { PDFViewerApplication?: { pdfViewer?: { pagesRotation: number } } }
    ).PDFViewerApplication;
    if (PDFViewerApplication?.pdfViewer) {
      PDFViewerApplication.pdfViewer.pagesRotation += 90;
    }
  }
}
